/* eslint-disable no-console */
import type { RumEvent } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';

import {
  APP_IS_DEVELOPMENT,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_RUM_CLIENT_TOKEN,
  DATADOG_RUM_DEFAULT_PRIVACY_LEVEL,
  DATADOG_RUM_ENABLED,
  DATADOG_RUM_ENV,
  DATADOG_RUM_MANUAL_REPLAY_CONTROL,
  DATADOG_RUM_SERVICE,
  DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
  DATADOG_RUM_SESSION_SAMPLE_RATE,
  DATADOG_RUM_SITE,
  DATADOG_RUM_TRACK_USER_INTERACTIONS,
  DATADOG_RUM_VERSION,
} from '../../configs/datadog';
import { logError } from '../logging';

import { normalizeRumPath } from './useRumViewTracking';

// RUM URL fields are sent verbatim (defaultPrivacyLevel only masks DOM text, not
// URLs), so strip identifiers before they leave the browser: drop query/hash and
// replace dynamic path segments with their route pattern.
function sanitizeRumUrl(url: string): string {
  try {
    const sanitizedUrl = new URL(url);
    sanitizedUrl.search = '';
    sanitizedUrl.hash = '';
    sanitizedUrl.pathname = normalizeRumPath(sanitizedUrl.pathname);
    return sanitizedUrl.toString();
  } catch {
    return url;
  }
}

export function initDatadogRum(): void {
  if (!DATADOG_RUM_ENABLED || !DATADOG_RUM_APPLICATION_ID || !DATADOG_RUM_CLIENT_TOKEN) {
    if (APP_IS_DEVELOPMENT) {
      console.log('🐕 Datadog RUM: Disabled', {
        enabled: DATADOG_RUM_ENABLED,
        hasApplicationId: Boolean(DATADOG_RUM_APPLICATION_ID),
        hasClientToken: Boolean(DATADOG_RUM_CLIENT_TOKEN),
      });
    }
    return;
  }

  try {
    if (APP_IS_DEVELOPMENT) {
      console.log('🐕 Datadog RUM: Initializing...', {
        env: DATADOG_RUM_ENV,
        sessionSampleRate: DATADOG_RUM_SESSION_SAMPLE_RATE,
        sessionReplaySampleRate: DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
        trackUserInteractions: DATADOG_RUM_TRACK_USER_INTERACTIONS,
        manualReplayControl: DATADOG_RUM_MANUAL_REPLAY_CONTROL,
      });
    }

    datadogRum.init({
      applicationId: DATADOG_RUM_APPLICATION_ID,
      clientToken: DATADOG_RUM_CLIENT_TOKEN,
      site: DATADOG_RUM_SITE,
      service: DATADOG_RUM_SERVICE,
      env: DATADOG_RUM_ENV,
      version: DATADOG_RUM_VERSION,
      sessionSampleRate: DATADOG_RUM_SESSION_SAMPLE_RATE,
      sessionReplaySampleRate: DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
      // Views are named manually (via useRumViewTracking) so overlay routes get
      // correct names instead of RUM's URL-based auto-views.
      trackViewsManually: true,
      trackResources: true,
      trackUserInteractions: DATADOG_RUM_TRACK_USER_INTERACTIONS,
      trackLongTasks: true,
      startSessionReplayRecordingManually: DATADOG_RUM_MANUAL_REPLAY_CONTROL,
      // Mask action names so click heatmaps never leak PII from member rows;
      // elements with an explicit data-dd-action-name override with safe strings.
      enablePrivacyForActionName: true,
      defaultPrivacyLevel: DATADOG_RUM_DEFAULT_PRIVACY_LEVEL,
      beforeSend: (event: RumEvent) => {
        // Sanitize every URL field so identifiers never reach Datadog. Datadog's
        // beforeSend requires mutating the event in place (it returns a boolean).
        /* eslint-disable no-param-reassign */
        event.view.url = sanitizeRumUrl(event.view.url);
        if (event.view.referrer) {
          event.view.referrer = sanitizeRumUrl(event.view.referrer);
        }
        if (event.type === 'resource') {
          event.resource.url = sanitizeRumUrl(event.resource.url);
        }
        if (event.type === 'error' && event.error.resource) {
          event.error.resource.url = sanitizeRumUrl(event.error.resource.url);
        }
        /* eslint-enable no-param-reassign */

        return true;
      },
    });

    if (APP_IS_DEVELOPMENT) {
      console.log('🐕 Datadog RUM: Successfully initialized');
    }
  } catch (e) {
    const eIsError = e instanceof Error;
    const error = eIsError ? e : new Error(String(e));
    if (!eIsError) {
      error.cause = e;
    }
    logError(error, {
      context: 'datadog_rum_init',
      isHandled: true,
      severity: 'error',
    });

    if (APP_IS_DEVELOPMENT) {
      console.error('🐕 Datadog RUM: Initialization failed:', error);
    }
  }
}
